<template>
  <!-- Полоса под верхней панелью: слева путь до страницы, справа — состояние
       связи. Оба блока служебные, поэтому живут отдельно от рабочей области
       и не спорят с заголовком списка. -->
  <div class="sts-topline">
    <!-- На странице записи путь остаётся и на телефоне: он возвращает к списку. -->
    <div v-if="!$vuetify.breakpoint.smAndDown || routeMeta.mobileBreadcrumb" class="sts-topline__inner">
      <v-breadcrumbs
        v-if="notMainPath"
        class="sts-crumbs"
        :items="breadcrumb_items"
      >
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item :to="item.disabled ? undefined : item.href" :disabled="item.disabled" exact :aria-current="item.disabled ? 'page' : undefined">
            {{ item.text }}
          </v-breadcrumbs-item>
        </template>
        <template v-slot:divider>
          <v-icon small>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>
      <span v-else class="sts-crumbs sts-crumbs--single">Главная</span>

      <div v-if="!$vuetify.breakpoint.smAndDown" class="sts-topline__right">
        <MonitorSize class="hidden-xs-only"/>

        <v-tooltip
          v-for="link in links"
          :key="link.channel"
          bottom
        >
          <template v-slot:activator="{ on }">
            <span class="sts-link" :class="`sts-link--${link.state}`" v-on="on">
              <span class="sts-link__dot"></span>
              <span class="sts-link__name">{{ link.title }}</span>
              {{ link.label }}
            </span>
          </template>
          <span>{{ link.hint }}</span>
        </v-tooltip>
      </div>
    </div>

    <div v-if="statisticRoute" class="sts-topline__stats">
      <DayStatistic />
    </div>
  </div>
</template>

<script>

// Оба компонента задают высоту полосы: пока они грузились отдельными чанками,
// полоса подрастала уже после первого кадра и сдвигала страницу. Сводка за день
// стоит над журналом операций и роняла его на полсотни пикселей.
import DayStatistic from "./Helpers/DayStatistic.vue";
import MonitorSize from "./MonitorSize.vue";

// Названия групп бокового меню — их же показываем в пути.
const SECTIONS = {
  documents: 'Работа с документами',
  settings: 'Настройки и справочники',
  admin: 'Настройки администратора'
}

export default {
  name: 'BlockTopContent',
  components: {
    DayStatistic,
    MonitorSize
  },
  data () {
    return {
      breadcrumb_items: [],
      // $route до конца навигации указывает на прежний маршрут: страницы
      // грузятся отдельными чанками, и роутер подтверждает переход только
      // после загрузки. Полоса из-за этого узнавала о блоке статистики с
      // опозданием и, подрастая, роняла страницу вниз. Разрешаем маршрут по
      // адресу сами — он известен с первого кадра.
      routeMeta: this.$router.resolve(window.location.pathname).route.meta || {}
    }
  },
  watch: {
    currentRoute: {
      handler() {
        this.updateBreadcrumb()
      }
    },
    '$route' (to) {
      this.routeMeta = to.meta || {}
    }
  },
  mounted() {
    this.updateBreadcrumb()
  },
  computed: {
    // Состояние каналов живёт в модуле cable, а не в корневом сторе:
    // одноимённые поля там есть, но их никто не обновляет.
    links() {
      return [
        { channel: 'WorkShiftChannel', title: 'Смена' },
        { channel: 'ReportChannel', title: 'Отчёты' }
      ].map(({ channel, title }) => {
        const status = this.$store.getters['cable/channelStatus'](channel)
        const state = status === 'connected' ? 'on'
          : status === 'connecting' ? 'wait'
          : 'off'

        return {
          channel,
          title,
          state,
          label: state === 'on' ? 'онлайн' : state === 'wait' ? 'связь…' : 'офлайн',
          hint: state === 'on' ? `${title}: канал на связи`
            : state === 'wait' ? `${title}: идёт подключение`
            : status === 'rejected' ? `${title}: сервер отклонил подключение — проверьте вход`
            : `${title}: соединение потеряно`
        }
      })
    },
    currentRoute() {
      return this.$route.path
    },
    notMainPath() {
      return this.$route.path !== '/'
    },
    statisticRoute() {
      return this.routeMeta.statistic
    },
    companyRoute() {
      return this.routeMeta.company
    }
  },
  methods: {
    // Путь собирается по иерархии из роутера: раздел меню (meta.section),
    // затем цепочка родительских страниц (meta.parent), затем текущая.
    // Раздел некликабелен — у групп меню нет собственной страницы.
    updateBreadcrumb () {
      const meta = this.$route.meta || {}

      if (this.$route.path === '/') {
        this.breadcrumb_items = []
        return
      }

      const crumbs = [{ text: 'Lorus', href: '/', disabled: false }]

      if (meta.section && SECTIONS[meta.section]) {
        crumbs.push({ text: SECTIONS[meta.section], href: '', disabled: true })
      }

      crumbs.push(...this.parentChain(meta))
      const currentLabel = meta.recordParam ? `${meta.name} ${this.$route.params[meta.recordParam]}` : meta.name
      crumbs.push({ text: currentLabel, href: this.$route.path, disabled: true })

      this.breadcrumb_items = crumbs
    },
    // Поднимаемся по meta.parent до корня. Ограничение по глубине — страховка
    // от кольца, если родители однажды окажутся замкнутыми друг на друга.
    parentChain (meta) {
      const routes = this.$router.options.routes
      const chain = []
      let parentName = meta.parent || (meta.parents && meta.parents[0])
      let depth = 0

      while (parentName && depth < 5) {
        const parent = routes.find(r => r.name === parentName)
        if (!parent) break

        chain.unshift({ text: parent.meta.name, href: parent.path, disabled: false })
        parentName = parent.meta.parent
        depth += 1
      }

      return chain
    },
  }
}
</script>
